import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { toast } from 'sonner';
import {
  Wallet, ArrowUpRight, ArrowDownLeft, Clock, CheckCircle, 
  AlertCircle, CreditCard, Building, RefreshCw, TrendingUp, TrendingDown
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Transaction List Item
const TransactionItem = ({ transaction }) => {
  const isIncoming = transaction.amount > 0;
  const Icon = isIncoming ? ArrowDownLeft : ArrowUpRight;
  
  const statusConfig = {
    completed: { color: 'text-green-600', bg: 'bg-green-50' },
    pending: { color: 'text-yellow-600', bg: 'bg-yellow-50' },
    failed: { color: 'text-red-600', bg: 'bg-red-50' },
    rejected: { color: 'text-red-600', bg: 'bg-red-50' }
  };

  const status = statusConfig[transaction.status] || statusConfig.pending;

  return (
    <div className="flex items-center justify-between p-3 border-b last:border-0 hover:bg-gray-50">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-full ${isIncoming ? 'bg-green-100' : 'bg-red-100'}`}>
          <Icon className={`w-4 h-4 ${isIncoming ? 'text-green-600' : 'text-red-600'}`} />
        </div>
        <div>
          <p className="font-medium text-sm">{transaction.description}</p>
          <p className="text-xs text-gray-500">
            {new Date(transaction.created_at).toLocaleString('ro-RO')}
          </p>
        </div>
      </div>
      <div className="text-right">
        <p className={`font-semibold ${isIncoming ? 'text-green-600' : 'text-red-600'}`}>
          {isIncoming ? '+' : ''}{transaction.amount.toFixed(2)} {transaction.currency}
        </p>
        <Badge variant="outline" className={`text-xs ${status.color}`}>
          {transaction.status}
        </Badge>
      </div>
    </div>
  );
};

// Wallet Component
export const WalletCard = ({ compact = false }) => {
  const { getAuthHeaders } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawMethod, setWithdrawMethod] = useState('bank_transfer');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchWallet();
  }, []);

  const fetchWallet = async () => {
    try {
      const res = await fetch(`${API}/wallet`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setWallet(data);
      }
    } catch (e) {
      console.error('Failed to fetch wallet:', e);
    } finally {
      setLoading(false);
    }
  };

  const requestWithdrawal = async (e) => {
    e.preventDefault();
    const amount = parseFloat(withdrawAmount);
    
    if (!amount || amount <= 0) {
      toast.error('Introduceți o sumă validă');
      return;
    }

    if (amount > wallet.available_balance) {
      toast.error('Fonduri insuficiente');
      return;
    }

    setProcessing(true);
    try {
      const res = await fetch(`${API}/wallet/withdraw`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ amount, method: withdrawMethod })
      });

      if (res.ok) {
        toast.success('Cerere de retragere trimisă');
        setShowWithdraw(false);
        setWithdrawAmount('');
        fetchWallet();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Eroare la retragere');
      }
    } catch (e) {
      toast.error('Eroare la retragere');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return <div className="animate-pulse p-4 bg-gray-100 rounded-lg h-32"></div>;
  }

  if (!wallet) {
    return null;
  }

  if (compact) {
    return (
      <div className="p-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg text-white">
        <div className="flex items-center gap-2 mb-2">
          <Wallet className="w-5 h-5" />
          <span className="text-sm opacity-90">Portofel</span>
        </div>
        <p className="text-2xl font-bold">{wallet.available_balance.toFixed(2)} EUR</p>
        {wallet.pending_balance > 0 && (
          <p className="text-sm opacity-75">
            <Clock className="w-3 h-3 inline mr-1" />
            {wallet.pending_balance.toFixed(2)} EUR în așteptare
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg text-white">
          <div className="flex items-center gap-2 mb-2">
            <Wallet className="w-5 h-5" />
            <span className="text-sm opacity-90">Sold Disponibil</span>
          </div>
          <p className="text-3xl font-bold">{wallet.available_balance.toFixed(2)}</p>
          <p className="text-sm opacity-75">EUR</p>
        </div>

        <div className="p-4 bg-white border rounded-lg">
          <div className="flex items-center gap-2 mb-2 text-yellow-600">
            <Clock className="w-5 h-5" />
            <span className="text-sm">În Escrow</span>
          </div>
          <p className="text-2xl font-bold text-gray-800">{wallet.pending_balance.toFixed(2)}</p>
          <p className="text-sm text-gray-500">EUR</p>
        </div>

        <div className="p-4 bg-white border rounded-lg">
          <div className="flex items-center gap-2 mb-2 text-green-600">
            <TrendingUp className="w-5 h-5" />
            <span className="text-sm">Total Câștigat</span>
          </div>
          <p className="text-2xl font-bold text-gray-800">{wallet.total_earned.toFixed(2)}</p>
          <p className="text-sm text-gray-500">EUR</p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <Button onClick={() => setShowWithdraw(true)} disabled={wallet.available_balance <= 0}>
          <ArrowUpRight className="w-4 h-4 mr-2" />
          Retrage Fonduri
        </Button>
        <Button variant="outline" onClick={fetchWallet}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizează
        </Button>
      </div>

      {/* Withdrawal Form */}
      {showWithdraw && (
        <div className="p-4 border rounded-lg bg-gray-50">
          <h4 className="font-medium mb-4">Retrage Fonduri</h4>
          <form onSubmit={requestWithdrawal} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Sumă (EUR)</label>
              <Input
                type="number"
                step="0.01"
                max={wallet.available_balance}
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                placeholder="0.00"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Disponibil: {wallet.available_balance.toFixed(2)} EUR
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Metoda de Retragere</label>
              <select
                value={withdrawMethod}
                onChange={(e) => setWithdrawMethod(e.target.value)}
                className="w-full p-2 border rounded"
              >
                <option value="bank_transfer">Transfer Bancar</option>
                <option value="paypal">PayPal</option>
                <option value="revolut">Revolut</option>
              </select>
            </div>

            <div className="flex gap-2">
              <Button type="submit" disabled={processing}>
                {processing ? 'Se procesează...' : 'Trimite Cerere'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowWithdraw(false)}>
                Anulează
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Transaction History */}
      <div className="border rounded-lg">
        <div className="p-3 border-b bg-gray-50">
          <h4 className="font-medium">Istoric Tranzacții</h4>
        </div>
        {wallet.transactions && wallet.transactions.length > 0 ? (
          <div className="max-h-80 overflow-y-auto">
            {wallet.transactions.map((tx, i) => (
              <TransactionItem key={tx.transaction_id || i} transaction={tx} />
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500">
            <Wallet className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Nicio tranzacție încă</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Escrow Funding Component
export const EscrowFunding = ({ escrowId, amount, onFunded }) => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);

  const fundEscrow = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/wallet/fund-escrow/${escrowId}`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({})
      });

      if (res.ok) {
        const data = await res.json();
        toast.success('Fonduri securizate în escrow');
        if (onFunded) onFunded(data);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Eroare la securizare');
      }
    } catch (e) {
      toast.error('Eroare la securizare');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 border rounded-lg bg-yellow-50 border-yellow-200">
      <div className="flex items-center gap-2 mb-2">
        <AlertCircle className="w-5 h-5 text-yellow-600" />
        <h4 className="font-medium text-yellow-800">Securizare Fonduri Necesară</h4>
      </div>
      <p className="text-sm text-yellow-700 mb-3">
        Pentru a începe colaborarea, trebuie să securizezi fondurile în escrow.
        Suma va fi reținută până la finalizarea colaborării.
      </p>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-2xl font-bold text-yellow-800">{amount.toFixed(2)} EUR</p>
          <p className="text-xs text-yellow-600">Total de securizat</p>
        </div>
        <Button onClick={fundEscrow} disabled={loading} className="bg-yellow-600 hover:bg-yellow-700">
          {loading ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <CreditCard className="w-4 h-4 mr-2" />
          )}
          Securizează Acum
        </Button>
      </div>
    </div>
  );
};

export default WalletCard;
